import React, { useState, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import cn from 'classnames';
// eslint-disable-next-line unused-imports/no-unused-imports
import tinymce from 'tinymce/tinymce';

// Import TinyMCE

// // Default icons are required for TinyMCE 5.3 or above
import 'tinymce/icons/default/index';

// // A theme is also required
import 'tinymce/themes/silver';

// // Any plugins you want to use has to be imported
import 'tinymce/plugins/paste';
import 'tinymce/plugins/lists';

import style from './TinyMCEEditor.module.scss';

const TinyMCEEditor = (props) => {
    const {
        height,
        initial,
        onChange,
        onChangeField,
        name,
        label,
        error,
        charLimit,
        containerRef,
        classes,
        placeholder,
        resize,
        anchorId,
        editorId,
        hideCharCounter = false,
    } = props;
    const { t } = useTranslation();
    const [localError, setLocalError] = useState('');
    const handleEditorChange = (content) => {
        if (onChangeField) onChangeField({ target: { name, value: content || '<p></p>' } });
        if (onChange) onChange(content || '<p></p>');
    };
    const init = {
        height,
        menubar: false,
        statusbar: false,
        plugins: ['lists paste'],
        toolbar:
            'formatselect fontselect |' +
            ' bold italic strikethrough backcolor forecolor |' +
            ' alignleft aligncenter alignright alignjustify | ' +
            'bullist numlist',
        resize,
        placeholder,
        cleanup: true,
        branding: false,
        theme_advanced_default_foreground_color: '#000000',
        skin_url: `${process.env.PUBLIC_URL}/skins`,
    };

    if (charLimit > 0) {
        init.maxChars = charLimit;
        init.setup = (ed) => {
            const allowedKeys = [8, 37, 38, 39, 40, 46];
            ed.on('keydown', (e) => {
                if (allowedKeys.includes(e.keyCode)) {
                    return true;
                }

                if (getEditorContentLength() >= init.maxChars) {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                }
            });

            ed.on('keyup', (e) => {
                updateCharCounter(getEditorContentLength());
            });
        };
        init.init_instance_callback = () => {
            updateCharCounter(getEditorContentLength());
        };
        init.paste_preprocess = (plugin, args) => {
            const editor = window.tinymce.get(window.tinymce.activeEditor.id);
            const len = editor.contentDocument.body.innerText.length;
            const text = args.content.replace(/<\/?\s*[^>]*>/gi, '');

            if (len - 1 + text.length > editor.settings.maxChars) {
                setLocalError((e) => t('pasteError'));
                setTimeout(() => setLocalError((e) => ''), 3000);
                args.content = '';
            } else {
                updateCharCounter(len + text.length);
            }
        };
    }

    return (
        <div className={cn(style.wrapper, classes.wrapper)} ref={containerRef} id={anchorId}>
            {label && <div className={cn(style.label, classes.label, { [style.withError]: error })}>{label}</div>}
            {charLimit > 0 && !hideCharCounter && (
                <div className={style.charCounter}>
                    <span id='editor-char-count' />/<span>{charLimit}</span>
                </div>
            )}
            <div className={cn(style.editorWrapper, { [style.withError]: !!error })}>
                <Editor
                    apiKey='footu8dp2ucvcveza8cf9kk6bjijurrr7ykqo6yabmp7wp5j'
                    value={initial}
                    init={init}
                    onEditorChange={handleEditorChange}
                    id={editorId || 'editor'}
                />
            </div>

            {error && <div className={style.error}>{error}</div>}
            {localError && <div className={style.error}>{localError}</div>}
        </div>
    );
};

TinyMCEEditor.propTypes = {
    initial: PropTypes.string,
    height: PropTypes.number,
    onChange: PropTypes.func,
    onChangeField: PropTypes.func,
    name: PropTypes.string,
    label: PropTypes.string,
    charLimit: PropTypes.number,
    classes: PropTypes.shape({
        wrapper: PropTypes.string,
        label: PropTypes.string,
    }),
    placeholder: PropTypes.string,
    resize: PropTypes.bool,
    anchorId: PropTypes.string,
};

TinyMCEEditor.defaultProps = {
    height: 200,
    onChange: () => {},
    onChangeField: () => {},
    charLimit: 0,
    classes: {},
    placeholder: '',
    resize: false,
};

function getEditorContentLength() {
    const content = window.tinymce.get(window.tinymce.activeEditor.id).contentDocument.body.innerText;

    return content === '\n' ? 0 : content.length;
}

function updateCharCounter(len) {
    const counter = document.querySelector('#editor-char-count');

    if (!counter) return;

    counter.innerHTML = len;
}

export default forwardRef((props, ref) => <TinyMCEEditor {...props} containerRef={ref} />);
